import { CdkCopyToClipboard } from '@angular/cdk/clipboard';
import { NgTemplateOutlet } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	computed,
	DestroyRef,
	effect,
	inject,
	Injector,
	input,
	OnInit,
	output,
	signal,
	TemplateRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, FormsModule, FormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { ClassicEditor } from '@/common/editor/classic-editor';
import { debounceTime, filter, finalize, map, Observable, switchMap } from 'rxjs';
import { take, tap } from 'rxjs/operators';

import { InfoboxService } from '../infobox/infobox.service';
import { TemplateTransformerService } from './template-transformer/template-transformer.service';
import { templateVariableValidator } from './forms-validators/template-variable-validator';
import { htmlTagsValidator } from './forms-validators/html-tags-validator';
import {
	BulkSendProgress,
	MessageGenerateAdapter,
	MessageGenerateEditorMode,
	MessageTemplate,
} from './message-generate.model';
import { MailListValidatorDirective } from '@/_utils/validators/mail-list-validator.directive';

export enum MessageType {
	SMS = 'SMS',
	EMAIL = 'EMAIL',
}

@Component({
	selector: 'app-message-generate',
	standalone: true,
	imports: [
		ReactiveFormsModule,
		MatInputModule,
		MatFormFieldModule,
		MatButtonModule,
		MatCheckboxModule,
		CdkCopyToClipboard,
		MatProgressSpinnerModule,
		MatSelectModule,
		FormsModule,
		RouterModule,
		MatProgressBarModule,
		NgTemplateOutlet,
		MailListValidatorDirective,
		MatTooltipModule,
		CKEditorModule,
	],
	templateUrl: './message-generate.component.html',
	styleUrl: './message-generate.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MessageGenerateComponent<TPreview, TSendResult = void> implements OnInit {
	readonly itemsIds = input.required<number[]>();
	readonly type = input.required<MessageType>();

	readonly adapter = input.required<MessageGenerateAdapter<TPreview, TSendResult>>();
	readonly showInOneMailToggle = input(false);
	readonly inOneMailToggleEnabled = input(false);
	readonly inOneMailDisabledTooltip = input('');
	readonly showCcField = input(true);
	readonly editorMode = input<MessageGenerateEditorMode>('text');

	readonly previewTemplate = input<TemplateRef<{
		$implicit: TPreview[];
		previewLoader: boolean;
		attachmentsByItem: Record<number, File[]>;
		previewErrors: unknown[];
	}> | null>(null);
	readonly attachmentsByItem = input<Record<number, File[]>>({});
	readonly defaultCc = input('');
	readonly closeDialog = output<void>();
	readonly templateChange = output<MessageTemplate | null>();

	readonly templateControl = new FormControl('', { nonNullable: true });
	readonly ccControl = new FormControl('', { nonNullable: true });

	readonly templates = signal<MessageTemplate[]>([]);
	readonly loader = signal(false);
	readonly sendProgress = signal<number | null>(null);
	readonly previewLoader = signal(false);
	readonly selectedTemplate = signal<MessageTemplate | null>(null);
	readonly previewList = signal<TPreview[]>([]);
	readonly previewErrorsList = signal<unknown[]>([]);
	readonly inOneMail = signal(false);

	readonly propertiesKeys = computed(() =>
		this.templateTransformerService.properties().map((v) => `{{ ${v.displayValue} }}`),
	);

	readonly isEmail = computed(() => this.type() === MessageType.EMAIL);
	readonly isCkEditorMode = computed(() => this.editorMode() === 'ckEditor');

	readonly editor = ClassicEditor;
	readonly editorConfig = {
		toolbar: ['heading', '|', 'bold', 'italic', 'bulletedList', 'numberedList', '|', 'undo', 'redo'],
	};

	readonly MessageType = MessageType;

	private readonly snackBar = inject(MatSnackBar);
	private readonly infoboxService = inject(InfoboxService);
	private readonly injector = inject(Injector);
	private readonly destroyRef = inject(DestroyRef);
	private readonly templateTransformerService = inject(TemplateTransformerService);
	private readonly cdr = inject(ChangeDetectorRef);

	ngOnInit(): void {
		this.initializeTemplateChange();
		this.initializeValidatorsUpdate();
		this.initializeDefaultCc();
		this.initializeTemplatesRefresh();
		this.initTemplatePreviewListener();
	}

	initializeTemplatesRefresh(): void {
		effect(
			() => {
				const inOneMail = this.showInOneMailToggle() ? this.inOneMail() : false;
				this.adapter().setInOneMail?.(inOneMail);
				this.resetMessageState();
				this.fetchTemplates();
			},
			{ injector: this.injector, allowSignalWrites: true },
		);
	}

	initializeTemplateChange(): void {
		effect(
			() => {
				this.templateChange.emit(this.selectedTemplate());
				const rawContent = this.selectedTemplate()?.content ?? '';
				const replacedContent = this.templateTransformerService.replaceValuesWithKeys(rawContent);

				if (this.templateControl.value !== replacedContent) {
					this.templateControl.setValue(replacedContent);
					this.fetchTemplatePlaceholders();
				}
			},
			{ injector: this.injector, allowSignalWrites: true },
		);
	}

	initializeValidatorsUpdate(): void {
		effect(
			() => {
				this.templateControl.setValidators([
					Validators.required,
					templateVariableValidator(this.propertiesKeys()),
					htmlTagsValidator(),
				]);
				this.templateControl.updateValueAndValidity();
			},
			{ injector: this.injector },
		);
	}

	initializeDefaultCc(): void {
		effect(
			() => {
				const defaultCc = this.defaultCc().trim();

				if (!this.isEmail() || !defaultCc || !this.showCcField()) {
					return;
				}

				if (this.ccControl.dirty && this.ccControl.touched) {
					return;
				}

				this.ccControl.setValue(defaultCc);
			},
			{ injector: this.injector },
		);
	}

	copied(code: string): void {
		this.snackBar.open(`Skopiowano ${code} do schowka`, '', { duration: 3000 });
	}

	initTemplatePreviewListener(): void {
		this.templateControl.valueChanges
			.pipe(
				filter(() => this.templateControl.valid),
				debounceTime(500),
				map((message) => this.templateTransformerService.replaceKeysWithValues(message)),
				switchMap((message) => this.loadPreview(message)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((preview) => {
				this.previewList.set(preview);
				this.previewErrorsList.set([...(this.adapter().getPreviewErrors?.() ?? [])]);
			});
	}

	send(): void {
		if (this.templateControl.invalid) {
			this.templateControl.markAsTouched();
			return;
		}

		if (this.isEmail() && this.showCcField() && this.ccControl.invalid) {
			this.ccControl.markAsTouched();
			return;
		}

		this.loader.set(true);
		this.sendProgress.set(null);

		const cc = this.isEmail() ? this.parseMails(this.ccControl.value) : undefined;

		const attachments = this.attachmentsByItem();

		this.adapter()
			.sendBulk(this.previewList(), cc, attachments)
			.pipe(
				tap((value) => {
					const progress = (value as BulkSendProgress)?.progress;
					if (typeof progress === 'number') {
						this.sendProgress.set(progress);
						this.cdr.detectChanges();
					}
				}),
				filter((value) => {
					const progress = (value as BulkSendProgress)?.progress;
					return typeof progress !== 'number' || progress === 100;
				}),
				take(1),
				tap(() => {
					this.infoboxService.success('Wiadomości wysłane pomyślnie');
					this.closeDialog.emit();
				}),
				finalize(() => {
					this.loader.set(false);
					this.sendProgress.set(null);
				}),
			)
			.subscribe();
	}

	onInOneMailChange(event: MatCheckboxChange): void {
		this.inOneMail.set(event.checked);
	}

	private parseMails(value: string | null | undefined): string[] {
		if (!value) {
			return [];
		}
		return value
			.split(',')
			.map((v) => v.trim())
			.filter((v) => v.length > 0);
	}

	private loadPreview(message: string): Observable<TPreview[]> {
		this.previewLoader.set(true);
		const previewPayload =
			this.type() === MessageType.EMAIL
				? {
						itemsIds: this.itemsIds(),
						message,
						subject: this.selectedTemplate()?.subject,
						template: this.selectedTemplate()?.id,
					}
				: { itemsIds: this.itemsIds(), message };

		return this.adapter()
			.createPreview(previewPayload)
			.pipe(finalize(() => this.previewLoader.set(false)));
	}

	private fetchTemplates(): void {
		this.loader.set(true);
		this.adapter()
			.fetchTemplates()
			.pipe(finalize(() => this.loader.set(false)))
			.subscribe((templates) => this.templates.set(templates));
	}

	private resetMessageState(): void {
		this.templates.set([]);
		this.selectedTemplate.set(null);
		this.templateControl.setValue('');
		this.templateControl.markAsPristine();
		this.templateControl.markAsUntouched();
		this.clearPreview();
		this.previewLoader.set(false);
		this.templateTransformerService.properties.set([]);
	}

	private fetchTemplatePlaceholders(): void {
		const templateId = this.selectedTemplate()?.id;
		if (!templateId) {
			return;
		}

		this.loader.set(true);
		this.adapter()
			.fetchPlaceholders(templateId)
			.pipe(finalize(() => this.loader.set(false)))
			.subscribe((placeholders) => this.templateTransformerService.properties.set(placeholders));
	}

	private clearPreview(): void {
		this.previewList.set([]);
		this.previewErrorsList.set([]);
	}
}
